import type { QueryClient, QueryKey } from "@tanstack/react-query";

// Query-cache registry — the ONE module that knows cache keys. The ESLint gate
// in eslint.config.mjs bans useQueryClient outside src/hooks + src/api and
// inline key arrays everywhere except here, so this module holds the whole
// dependency graph.
//
// Two deliberate properties:
//  - todoQueue/todosDone stay under the ["todos"] root, so any event that
//    touches to-dos ALSO refreshes the unified queue's virtual ICP-deadline
//    rows via prefix matching (there is intentionally no "reminders" key).
//  - Events invalidate by PREFIX, not exact keys: the dataset is tiny,
//    correctness beats thrift, and a prefix can never miss a key variant.

export const keys = {
  me: (): QueryKey => ["me"],
  settings: (): QueryKey => ["settings"],
  homes: (): QueryKey => ["homes"],
  pushVapid: (): QueryKey => ["push-vapid"],
  templates: (): QueryKey => ["templates"],

  todos: (): QueryKey => ["todos"],
  todoQueue: (homeId = "", tag = ""): QueryKey => ["todos", "queue", homeId, tag],
  todosDone: (): QueryKey => ["todos", "done", "all"],

  agenda: (today: string): QueryKey => ["agenda", today],
  wrapUp: (date: string): QueryKey => ["wrap-up", date],
  handoverDraft: (homeId: string | null): QueryKey => ["handover-draft", homeId],
  dailyLog: (date: string): QueryKey => ["daily-log", date],

  residentsList: (homeId = "", status = ""): QueryKey => ["residents", homeId, status],
  resident: (id: string | null): QueryKey => ["resident", id], // callers gate with enabled: id !== null

  // One "rota" root per domain concept: a single prefix covers the family.
  rotaList: (month: string): QueryKey => ["rota", "list", month],
  rotaSummary: (month: string): QueryKey => ["rota", "summary", month],
  rotaImports: (month: string): QueryKey => ["rota", "imports", month],

  forecast: (weeks: string): QueryKey => ["forecast", weeks],

  // Global search keeps V1 shadow caches (deliberate per docs/03-UX-SPEC.md)
  // but spells every key through this registry.
  searchResidents: (): QueryKey => ["search-residents"],
  searchTodos: (): QueryKey => ["search-todos"],
};

/** Domain events (CONTEXT.md vocabulary) that can make cached data stale. */
export type CacheEvent =
  | "todoChanged"
  | "residentChanged"
  | "rotaChanged"
  | "homesChanged"
  | "templatesChanged"
  | "pushChanged";

// Prefixes are ROOTS only (["todos"], not ["todos","queue",""]) — TanStack
// prefix matching requires exact equality on the given parts, so a
// parametrized key with "" defaults would NOT match real keys.
const EVENT_STALE_PREFIXES: Record<CacheEvent, QueryKey[]> = {
  // Completing a to-do rewrites that day's done-strip and the wrap-up.
  todoChanged: [["todos"], ["agenda"], ["wrap-up"], ["daily-log"]],
  // A resident change can spawn/close to-dos, move deadlines (queue lives
  // under ["todos"]), and syncs update homes.lastSyncedAt. An ICP fill IS a
  // resident change.
  residentChanged: [["resident"], ["residents"], ["todos"], ["agenda"], ["wrap-up"], ["forecast"], ["homes"]],
  // Shifts render in the agenda and in the day-note done strip.
  rotaChanged: [["rota"], ["agenda"], ["daily-log"]],
  // homeName is serialized into nearly every response.
  homesChanged: [["homes"], ["residents"], ["resident"], ["todos"], ["agenda"], ["wrap-up"], ["forecast"], ["daily-log"], ["handover-draft"]],
  templatesChanged: [["templates"]],
  pushChanged: [["push-vapid"]],
};

/** Invalidate every query an event makes stale. Prefix-level by policy (see header). */
export function invalidateAfter(qc: QueryClient, event: CacheEvent): void {
  for (const prefix of EVENT_STALE_PREFIXES[event]) void qc.invalidateQueries({ queryKey: prefix });
}
